import type { AttachmentStyle, RelationshipContext, InteractionType } from './storage';

export const DEFUSION_PHRASES = [
  'To tylko myśl, nie fakt.',
  'Mój umysł próbuje mnie chronić.',
  'Nie muszę działać na podstawie tego impulsu.',
  'Ten uczucie jest tymczasowe.',
  'Mogę czuć to i mimo to wybrać inaczej.',
  'Moje myśli to nie rozkazy.',
  'Jestem więcej niż ta reakcja.',
  'To wzorzec, który znam — i mogę go obserwować.',
  'Moje ciało reaguje na zagrożenie, którego teraz nie ma.',
  'Mogę być z tym uczuciem bez natychmiastowego działania.',
  'Ta intensywność minie. Nie muszę jej zatrzymywać.',
  'Widzę tę myśl. Nie jestem tą myślą.',
];

export const GROUNDING_STEPS = [
  { label: 'Wdech', duration: 4, instruction: 'Oddychaj powoli przez nos… 1, 2, 3, 4' },
  { label: 'Zatrzymaj', duration: 4, instruction: 'Zatrzymaj oddech… 1, 2, 3, 4' },
  { label: 'Wydech', duration: 6, instruction: 'Wypuść powoli… 1, 2, 3, 4, 5, 6' },
];

export const REFLECTION_QUESTIONS = [
  'Czy ta decyzja przybliża cię do relacji, której chcesz?',
  'Czy działasz zgodnie ze swoją intencją, nie ze strachem?',
  'Co zrobiłaby osoba, którą chcesz być w tej relacji?',
  'Czy za 24 godziny będziesz zadowolony/a z tej decyzji?',
  'Czy to działanie wyraża twoje wartości, czy twoją obronę?',
];

export interface ActionOption {
  type: 'default' | 'neutral' | 'values';
  label: string;
  description: string;
  example: string;
}

export function getActions(
  style: AttachmentStyle,
  context: RelationshipContext,
  interaction: InteractionType
): ActionOption[] {
  if (style === 'avoidant') {
    return [
      {
        type: 'default',
        label: 'Automatyczna reakcja',
        description: 'Wycofanie, cisza, odcięcie',
        example: interaction === 'async' ?'Nie odpisywać teraz' :'Zamknąć się i czekać aż minie',
      },
      {
        type: 'neutral',
        label: 'Zatrzymanie',
        description: 'Nie działaj teraz — daj sobie chwilę',
        example: 'Powiedz: „Potrzebuję chwili, wróćmy do tego"',
      },
      {
        type: 'values',
        label: 'Działanie z wartości',
        description: 'Pozostań obecny, wyraź małą prawdę',
        example: interaction === 'sync' ?'Zostań w rozmowie 2 minuty dłużej i powiedz jedno szczere zdanie' :'Napisz jedną prostą, szczerą wiadomość zamiast milczeć',
      },
    ];
  }

  return [
    {
      type: 'default',
      label: 'Automatyczna reakcja',
      description: 'Gonienie, zalewanie emocjami, nadpisywanie',
      example: interaction === 'async' ?'Wysłać serię wiadomości, żeby uzyskać odpowiedź' :'Eskalować rozmowę, żeby rozwiązać wszystko teraz',
    },
    {
      type: 'neutral',
      label: 'Zatrzymanie',
      description: 'Odczekaj — nie decyduj teraz',
      example: 'Poczekaj 10 minut zanim odpiszesz lub zareagujesz',
    },
    {
      type: 'values',
      label: 'Działanie z wartości',
      description: 'Wyraź potrzebę spokojnie, bez presji',
      example: interaction === 'sync' ?'Nazwij uczucie: „Czuję się niepewnie i potrzebuję chwili"' :'Napisz jedną wiadomość wyrażającą potrzebę, nie żądanie',
    },
  ];
}

export function getContextLabel(ctx: RelationshipContext): string {
  const map: Record<RelationshipContext, string> = {
    partner: 'Partner / Partnerka',
    'ex-partner': 'Były partner / Była partnerka',
    work: 'Relacja zawodowa',
    parent: 'Rodzic',
  };
  return map[ctx];
}

export function getIntentionLabel(i: import('./storage').RelationshipIntention): string {
  const map = {
    end: 'Zakończyć relację',
    'build-new': 'Budować nową relację',
    improve: 'Poprawić obecną relację',
  };
  return map[i];
}

export function getStyleLabel(s: AttachmentStyle): string {
  return s === 'avoidant' ? 'Styl unikający' : 'Styl lękowy';
}

export function getInteractionLabel(t: InteractionType): string {
  const map: Record<InteractionType, string> = {
    sync: 'Rozmowa na żywo',
    async: 'Wiadomości / SMS',
    none: 'Brak interakcji',
  };
  return map[t];
}